import { createSelector } from '@reduxjs/toolkit';

import { hasTrussDesignPermission, isLoggedInUserEngineer } from '@paragon/login-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '..';
import { TrussEditsPanelType } from '../slices/panelDataSlice';
import { getInsertingObjectInfo } from './insertionObjectSelectors';
import { getSnappingUnderlayTakeoverMode } from './snappingUnderlaySelectors';

export const hasPermissionToViewCheckResults = createSelector(
  hasTrussDesignPermission,
  isLoggedInUserEngineer,
  (state: RootState) => state.application.openContext?.type === 'shared-component-set',
  (hasTrussDesign, isEngineer, isViewingSharedComponentSet) =>
    hasTrussDesign || isEngineer || isViewingSharedComponentSet,
);

export const getShouldAddItemPanelBeActive = createSelector(
  (state: RootState) => state.application.insertingObjectInfo?.type,
  (insertionObjectType) => {
    switch (insertionObjectType) {
      case 'bearing':
      case 'splice':
      case undefined:
        return false;

      default:
        return true;
    }
  },
);

export const getShouldAddItemPanelForceSidebarOpen = createSelector(
  (state: RootState) => state.application.insertingObjectInfo?.type,
  (insertionObjectType) => {
    switch (insertionObjectType) {
      case 'member':
      case 'referenceLine':
        return false;

      default:
        return true;
    }
  },
);

type RightSidebarMode =
  | 'replace-lumbers'
  | 'settings'
  | 'add-item'
  | TrussEditsPanelType
  | 'importing-component'
  | 'match-fascia-heights'
  | 'optimization'
  | 'rename-layout-components'
  | 'transform'
  | 'none'
  | 'normal';

export const getRightSidebarInfo = createSelector(
  (state: RootState) => state.panelData.rightToolBarOpen,
  getShouldAddItemPanelBeActive,
  getShouldAddItemPanelForceSidebarOpen,
  (state: RootState) => state.panelData.trussEditsPanel,
  (state: RootState) => state.application.importingComponentInfo != null,
  (state: RootState) => state.panelData.matchFasciaHeightsPanelOpen,
  (state: RootState) => state.panelData.settingsPanelActiveModelGuid != null,
  (state: RootState) => state.panelData.replaceMaterialsPanel != null,
  (state: RootState) => state.panelData.renameLayoutComponentsPanel.open,
  (state: RootState) => state.application.transformingObjectsInfo,
  getSnappingUnderlayTakeoverMode,
  (state: RootState) => state.panelData.optimizationPanelOpen,

  (
    rightSidebarManuallyOpened,
    addItemPanelActive,
    addItemPanelForcesSidebarOpen,
    trussEditsPanel,
    importingComponentPanelActive,
    matchFasciaHeightsPanelActive,
    settingsPanelActive,
    replaceLumbersPanelActive,
    renameLayoutComponentsPanelActive,
    transformationPanelActive,
    snappingUnderlayTakeoverMode,
    optimizationPanelActive,
  ): { mode: RightSidebarMode; canBeClosed: boolean } => {
    // can be opened from settings panel, so must be checked above it
    if (replaceLumbersPanelActive) return { mode: 'replace-lumbers', canBeClosed: false };

    // can be opened from add item panel, so must be checked above it
    if (settingsPanelActive) return { mode: 'settings', canBeClosed: false };

    if (addItemPanelActive && (rightSidebarManuallyOpened || addItemPanelForcesSidebarOpen)) {
      return { mode: 'add-item', canBeClosed: !addItemPanelForcesSidebarOpen };
    }

    if (trussEditsPanel) return { mode: trussEditsPanel, canBeClosed: false };
    if (importingComponentPanelActive) return { mode: 'importing-component', canBeClosed: false };
    if (matchFasciaHeightsPanelActive) return { mode: 'match-fascia-heights', canBeClosed: false };
    if (renameLayoutComponentsPanelActive) return { mode: 'rename-layout-components', canBeClosed: false };
    if (transformationPanelActive) return { mode: 'transform', canBeClosed: false };
    if (optimizationPanelActive) return { mode: 'optimization', canBeClosed: false };

    if (snappingUnderlayTakeoverMode) return { mode: 'normal', canBeClosed: false };
    if (rightSidebarManuallyOpened) return { mode: 'normal', canBeClosed: true };

    return { mode: 'none', canBeClosed: true };
  },
);

export const getShouldRightSidebarBeInLegacyMode = createSelector(
  getRightSidebarInfo,
  getInsertingObjectInfo,
  (rightSidebar, insertionInfo) => {
    switch (rightSidebar.mode) {
      case 'add-item': {
        switch (insertionInfo?.type) {
          case 'bearingEnvelope':
          case 'ceilingPlane':
          case 'ceilingTray':
          case 'featureArea':
          case 'featurePatternContainer':
          case 'workLine':
          case 'member':
          case 'referenceLine':
          case 'roofPlane':
          case 'trussEnvelope':
          case 'trussGroup':
            return false;

          default:
            return true;
        }
      }

      case 'settings':
        return true;

      case 'gable':
      case 'reweb-profile':
      case 'heel-macros':
      case 'importing-component':
      case 'match-fascia-heights':
      case 'optimization':
      case 'none':
      case 'normal':
      case 'overhang-macros':
      case 'rename-layout-components':
      case 'replace-lumbers':
      case 'reweb-zone':
      case 'span-macros':
      case 'stub':
      case 'transform':
        return false;

      default:
        assertExhaustiveSwitch(rightSidebar.mode);
    }
  },
);
